import { Dayjs } from 'dayjs';
import {
  FC,
  lazy,
  memo,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import * as React from 'react';
import { isMobile } from 'react-device-detect';
import { ErrorBoundary } from 'react-error-boundary';

import BetList from '@/components/layouts/Race/BetList/Singlerace';
import RaceCard from '@/components/layouts/Race/RaceCard';
import DigestButton from '@/components/layouts/Race/Singlerace/DigestButton';
import LiveButton from '@/components/layouts/Race/Singlerace/LiveButton';
import RaceInfo from '@/components/layouts/Race/Singlerace/RaceInfo';
import RaceInfoTypeSelector from '@/components/layouts/Race/Singlerace/RaceInfoTypeSelector';
import RaceProgramInfo from '@/components/layouts/Race/Singlerace/RaceProgramInfo';
import {
  LineForecastWrapper,
  LiveButtonContainer,
  LiveButtonWrapper,
  LiveMovieWrapper,
  RaceCardFooter,
  RaceInfoTableHead,
  RaceInfoTypeSelectorWrapper,
} from '@/components/templates/Velodrome/Race/Singlerace/pc/style';
import { useLayoutContext } from '@/contexts/layout';
import {
  BetStatus,
  Race,
  RaceEntryStatus,
  SingleRaceRefund,
} from '@/lib/apollo/types/gen/graphql';
import { BetMethod, RaceInfoType } from '@/modelTypes';
import { mq } from '@/utility/mediaQuery';

import { ModifyingDiv } from '../../style';
import { LayoutProps } from '../index';
import {
  BetListWrapper,
  BetSheetWrapper,
  CurrentRaceWrapper,
  EmptyText,
  EmptyWrapper,
  ListWrapper,
  RaceCardWrapper,
  RaceInfoWrapper,
  SheetWrapper,
  TableWrapper,
} from '../style';
import { CheckBoxWrapper, PrimaryBox, SecondaryBox } from './style';

const OrderList = lazy(() => import('@/components/layouts/History/OrderList'));
const CurrentRaceList = lazy(
  () => import('@/components/layouts/Race/Singlerace/CurrentRaceList'),
);
// TODO const LiveButton = lazy(() => import('@/components/layouts/Race/Singlerace/LiveButton'),);
// TODO const DigestButton = lazy(() => import('@/components/layouts/Race/Singlerace/DigestButton'),);
const LiveMovie = lazy(
  () => import('@/components/layouts/Race/Singlerace/LiveMovie'),
);
const OddsCard = lazy(
  () => import('@/components/layouts/Race/Singlerace/OddsCard'),
);
const RaceResult = lazy(
  () => import('@/components/layouts/Race/Singlerace/RaceResult'),
);
const BetSheet = lazy(
  () => import('@/components/layouts/Race/Singlerace/BetSheet'),
);
const LineForecast = lazy(() => import('@/components/layouts/Race/Forecast'));
const CheckMulti = lazy(
  () => import('@/components/layouts/Race/Singlerace/CheckMulti'),
);
const BetMethodSelector = lazy(
  () => import('@/components/layouts/Race/Singlerace/BetMethodSelector'),
);
const BetTypeSelector = lazy(
  () => import('@/components/layouts/Race/Singlerace/BetTypeSelector'),
);

const Content: React.FC<
  Pick<
    LayoutProps,
    | 'race'
    | 'raceProgram'
    | 'raceInfoType'
    | 'betMethod'
    | 'isBettable'
    | 'isFirstDay'
    | 'isMultiData'
    | 'betTypeList'
    | 'bettableData'
    | 'oddsListType'
    | 'oddsData'
    | 'modifyBetDetailKey'
    | 'entryRacerNoteList'
    | 'wheelAxisList'
    | 'firstThreeRacerData'
    | 'orderSingleraceData'
    | 'oddsQueryRefetch'
    | 'oddsUpdateTime'
    | 'setOddsListType'
    | 'setBettableData'
    | 'showEntryRacerNoteModal'
    | 'setIsMultiData'
    | 'setFirstThreeRacerData'
    | 'getScoreDetailOwn'
    | 'getRacerScoreByAttributes'
    | 'scoreDetailOwns'
    | 'scoreDetailMatchByBasic'
    | 'scoreDetailMatchByLine'
    | 'scoreByAttribute'
  >
> = memo(
  ({
    race,
    raceProgram,
    raceInfoType,
    betMethod,
    isBettable,
    isFirstDay,
    isMultiData,
    betTypeList,
    bettableData,
    oddsListType,
    oddsData,
    modifyBetDetailKey,
    entryRacerNoteList,
    wheelAxisList,
    firstThreeRacerData,
    orderSingleraceData,
    oddsQueryRefetch,
    oddsUpdateTime,
    setOddsListType,
    setBettableData,
    showEntryRacerNoteModal,
    setIsMultiData,
    setFirstThreeRacerData,
    getScoreDetailOwn,
    getRacerScoreByAttributes,
    scoreDetailOwns,
    scoreDetailMatchByBasic,
    scoreDetailMatchByLine,
    scoreByAttribute,
  }) => {
    /** 開催期限日時を取得：第二引数に指定した月数分加算して返却する */
    const getLimitHeldDateV2 = (heldDate: Dayjs, addMonth: number) => {
      return heldDate.add(addMonth, 'month').format('YYYY/MM/DD');
    };

    useEffect(() => {
      if (raceInfoType === RaceInfoType.Basic) {
        getScoreDetailOwn(
          race.id,
          raceProgram.heldDate.format('YYYY/MM/DD'),
          getLimitHeldDateV2(raceProgram.heldDate, -24), // 対戦成績：過去二年（24ヶ月）分を取得する
          0,
        );
      } else if (raceInfoType === RaceInfoType.VelodromeResult) {
        getScoreDetailOwn(
          race.id,
          raceProgram.heldDate.format('YYYY/MM/DD'),
          getLimitHeldDateV2(raceProgram.heldDate, -36), // 当場成績：過去三年（36ヶ月）分を取得する
          raceProgram.velodromeCode, // レースプログラム内の「競輪場コード」を使用
        );
      } else if (raceInfoType === RaceInfoType.Line) {
        getScoreDetailOwn(
          race.id,
          raceProgram.heldDate.format('YYYY/MM/DD'),
          getLimitHeldDateV2(raceProgram.heldDate, -36), // 当場成績：過去三年（36ヶ月）分を取得する
          0, // レースプログラム内の「競輪場コード」を使用
        );
        getRacerScoreByAttributes(race.id);
      } else {
        getScoreDetailOwn(
          race.id,
          raceProgram.heldDate.format('YYYY/MM/DD'),
          '',
          0,
        );
      }
    }, [raceInfoType, race]);

    const isDecided = useMemo(() => {
      return race.singleRaceRefunds.some(
        srr => srr.betStatus === BetStatus.Decision,
      );
    }, [race]);

    if (raceInfoType === RaceInfoType.Order) {
      return (
        <Suspense fallback={<></>}>
          {Object.values(orderSingleraceData).some(od => od.length > 0) ? (
            <ListWrapper>
              <OrderList
                orderData={orderSingleraceData}
                oddsData={oddsData.data}
                isDecided={isDecided}
              />
            </ListWrapper>
          ) : (
            <EmptyWrapper>
              <EmptyText>購入車券はありません</EmptyText>
            </EmptyWrapper>
          )}
          <RaceCard
            heldDate={raceProgram.heldDate}
            entryRacers={[]}
            isFirstDay={isFirstDay}
            raceInfoType={RaceInfoType.Empty}
          />
        </Suspense>
      );
    }
    if (raceInfoType === RaceInfoType.Odds || betMethod === BetMethod.Odds) {
      return (
        <Suspense fallback={<></>}>
          <OddsCard
            oddsData={oddsData}
            oddsQueryRefetch={oddsQueryRefetch}
            oddsUpdateTime={oddsUpdateTime}
            race={race}
            betType={betTypeList[BetMethod.Odds]}
            bettableList={bettableData[betMethod]}
            listType={oddsListType}
            isBettable={isBettable}
            modifyBetDetailKey={modifyBetDetailKey}
            setListType={setOddsListType}
            setBettableData={setBettableData}
          />
          <RaceCard
            heldDate={raceProgram.heldDate}
            entryRacers={[]}
            isFirstDay={isFirstDay}
            raceInfoType={RaceInfoType.Empty}
          />
        </Suspense>
      );
    }
    if (raceInfoType === RaceInfoType.Result) {
      return (
        <Suspense fallback={<></>}>
          <RaceResult
            heldDate={raceProgram.heldDate}
            isFirstDay={isFirstDay}
            entryRacers={race.entryRacers}
            entryRacerNoteList={entryRacerNoteList}
            showEntryRacerNoteModal={showEntryRacerNoteModal}
            singleRaceRefunds={race.singleRaceRefunds as SingleRaceRefund[]}
            odds={oddsData.data}
          />
          <RaceCardFooter>
            {race.lineForecast && (
              <LineForecastWrapper>
                <Suspense fallback={<></>}>
                  <LineForecast lineForecast={race.lineForecast} />
                </Suspense>
              </LineForecastWrapper>
            )}
          </RaceCardFooter>
          <RaceCard
            heldDate={raceProgram.heldDate}
            entryRacers={[]}
            isFirstDay={isFirstDay}
            raceInfoType={RaceInfoType.Empty}
          />
        </Suspense>
      );
    }
    return (
      <SheetWrapper>
        <RaceCardWrapper>
          <RaceCard
            heldDate={raceProgram.heldDate}
            isFirstDay={isFirstDay}
            entryRacers={race.entryRacers}
            entryRacerNoteList={entryRacerNoteList}
            showEntryRacerNoteModal={showEntryRacerNoteModal}
            raceInfoType={raceInfoType}
            scrollable
            scoreDetailOwns={scoreDetailOwns}
            scoreDetailMatchByBasic={scoreDetailMatchByBasic}
            scoreDetailMatchByLine={scoreDetailMatchByLine}
            scoreByAttribute={scoreByAttribute}
            lineForecast={
              race.lineForecast as NonNullable<Race['lineForecast']>
            }
          />
          <RaceCardFooter>
            {race.lineForecast && (
              <LineForecastWrapper>
                <Suspense fallback={<></>}>
                  <LineForecast lineForecast={race.lineForecast} />
                </Suspense>
              </LineForecastWrapper>
            )}
            {isBettable && (
              <CheckBoxWrapper>
                <Suspense fallback={<></>}>
                  <CheckMulti
                    betMethod={betMethod}
                    betTypeList={betTypeList}
                    isMultiData={isMultiData}
                    setIsMultiData={setIsMultiData}
                  />
                </Suspense>
              </CheckBoxWrapper>
            )}
          </RaceCardFooter>
        </RaceCardWrapper>
        {isBettable && (
          <BetSheetWrapper>
            <Suspense fallback={<></>}>
              <BetSheet
                race={race}
                betType={betTypeList[BetMethod.Wheel]}
                betMethod={betMethod}
                wheelAxisList={wheelAxisList}
                firstThreeRacerData={firstThreeRacerData}
                setFirstThreeRacerData={setFirstThreeRacerData}
              />
            </Suspense>
          </BetSheetWrapper>
        )}
      </SheetWrapper>
    );
  },
);

const RacePageLayoutPc: FC<LayoutProps> = props => {
  const { setLayoutStyles } = useLayoutContext();
  useLayoutEffect(() => {
    setLayoutStyles({
      showBetButton: false,
      customStyles: mq({
        maxWidth: ['100%', '100%', '100%', '920px', '1280px'],
      }),
    });
  }, []);

  const {
    race,
    raceProgram,
    racePrograms,
    raceInfoType,
    betMethod,
    isBettable,
    oddsData,
    betDataList,
    betPanelTicketCount,
    bettableData,
    betCountData,
    betTypeList,
    wheelAxisList,
    isMultiData,
    modifyBetDetailKey,
    addToBetPanel,
    cancelModifyTheBetDetail,
    isModifyingDisabled,
    setBetMethod,
    setIsOpenBetPanel,
    setBettableData,
    setBetCountData,
    setBetTypeList,
    setWheelAxisList,
    isNotUnregisteredRaces,
    velodrome,
    hasOrderTicket,
    isOrderProcessing,
  } = props;

  const [hasResultDetail, setHasResultDetail] = useState<boolean>(false);
  useEffect(() => {
    setHasResultDetail(false);

    if (race.raceEntryStatus === RaceEntryStatus.Unregistered) {
      return;
    }

    if (race && race.entryRacers) {
      race.entryRacers.forEach(er => {
        if (
          er.arrivalOrder != 0 &&
          er.arrivalOrder <= 3 &&
          er.finalStagePlayerTime != 0
        ) {
          // 順位が「1, 2, 3」で、「個人上りタイム」が有効値（0以外）なら、「結果詳細がある」判定
          setHasResultDetail(true);
        }

        if (er.arrivalOrder >= 4) {
          // 「4着」以上の順位が記録されているのであれば、「結果詳細がある」判定
          setHasResultDetail(true);
        }
      });
    } else {
      setHasResultDetail(false);
    }
  }, [race.entryRacers]);

  return (
    <>
      <PrimaryBox>
        <RaceInfoWrapper>
          <h1>
            <RaceProgramInfo raceProgram={raceProgram} />
          </h1>
          <RaceInfo
            raceProgram={raceProgram}
            race={race}
            betDataList={betDataList}
          />
        </RaceInfoWrapper>
        <CurrentRaceWrapper>
          <p>本日のレース</p>
          <Suspense fallback={<></>}>
            <CurrentRaceList
              raceProgramId={raceProgram.id}
              racePrograms={racePrograms}
              betDataList={betDataList}
              gap={8}
              gridTemplateColumns={[
                'repeat(auto-fit, minmax(118px, 1fr))',
                'repeat(auto-fit, minmax(118px, 1fr))',
                'repeat(auto-fit, minmax(118px, 1fr))',
                'repeat(auto-fit, minmax(118px, 1fr))',
                'repeat(5, 1fr)',
              ]}
            />
          </Suspense>
        </CurrentRaceWrapper>
      </PrimaryBox>
      <SecondaryBox>
        <TableWrapper>
          <RaceInfoTableHead>
            <RaceInfoTypeSelectorWrapper>
              <RaceInfoTypeSelector
                isBettable={isBettable}
                isCanceled={raceProgram.isCanceled || race.isCanceled}
                hasOrderTicket={hasOrderTicket}
                raceEntryStatus={race.raceEntryStatus}
                raceInfoType={raceInfoType}
              />
            </RaceInfoTypeSelectorWrapper>
          </RaceInfoTableHead>
          {isBettable && raceInfoType !== RaceInfoType.Order && (
            <Suspense fallback={<></>}>
              <BetMethodSelector
                betMethod={betMethod}
                isModifyingDisabled={isModifyingDisabled}
                setBetMethod={setBetMethod}
              />
            </Suspense>
          )}
          {(isBettable || raceInfoType === RaceInfoType.Odds) &&
            raceInfoType !== RaceInfoType.Order && (
              <Suspense fallback={<></>}>
                <BetTypeSelector
                  betMethod={
                    raceInfoType === RaceInfoType.Odds
                      ? BetMethod.Odds
                      : betMethod
                  }
                  releaseInfo={race.singleRaceReleaseInfo}
                  modifyBetDetailKey={modifyBetDetailKey}
                  betData={betDataList[race.id]}
                  isModifyingDisabled={isModifyingDisabled}
                  betTypeList={betTypeList}
                  setBetTypeList={setBetTypeList}
                  wheelAxisList={wheelAxisList}
                  setWheelAxisList={setWheelAxisList}
                />
              </Suspense>
            )}
          {Boolean(modifyBetDetailKey) && betMethod !== BetMethod.Odds && (
            <ModifyingDiv>修正中</ModifyingDiv>
          )}
          <Content {...props} />
        </TableWrapper>
        <BetListWrapper>
          {!isNotUnregisteredRaces && (
            <ErrorBoundary fallbackRender={() => <></>}>
              <LiveMovieWrapper>
                <Suspense fallback={<></>}>
                  <LiveMovie velodrome={velodrome} device="pc" />
                </Suspense>
              </LiveMovieWrapper>
            </ErrorBoundary>
          )}
          <BetList
            oddsData={oddsData}
            isBettable={isBettable}
            isProcessing={isOrderProcessing}
            bettableData={bettableData}
            setBettableData={setBettableData}
            setIsShowModal={setIsOpenBetPanel}
            modifyBetDetailKey={modifyBetDetailKey}
            betCountData={betCountData}
            setBetCountData={setBetCountData}
            isMultiData={isMultiData}
            addToBetPanel={addToBetPanel}
            cancelModifyTheBetDetail={cancelModifyTheBetDetail}
            betPanelTicketCount={betPanelTicketCount}
          />
          <LiveButtonWrapper>
            <LiveButtonContainer>
              {!isMobile &&
                race.raceEntryStatus === RaceEntryStatus.Unregistered && (
                  <Suspense fallback={<></>}>
                    <LiveButton velodrome={velodrome} device={'pc'} />
                  </Suspense>
                )}
              {!isMobile && hasResultDetail && (
                <Suspense fallback={<></>}>
                  <DigestButton
                    velodrome={velodrome}
                    device={'pc'}
                    heldDate={raceProgram.heldDate}
                    raceRank={raceProgram.gradeName}
                    raceNum={race.raceNum}
                  />
                </Suspense>
              )}
            </LiveButtonContainer>
          </LiveButtonWrapper>
        </BetListWrapper>
      </SecondaryBox>
    </>
  );
};

export default memo(RacePageLayoutPc);
